import {
  PointerEvent as ReactPointerEvent,
  useCallback,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

import { useIsFeatureFlagEnabled } from '@paradigm/features/src/feature-flags/store';
import { selectIsAuthenticated } from '@paradigm/store/auth';
import { UnifiedRfqTradeTapeProvider } from '@paradigm/features/src/unified-rfqs/contexts/unified-trade-tapes';

import {
  FEATURE_BLOCK_TERMINAL,
  useTerminalDrawerActions,
  useTerminalDrawerState,
} from '#/features/terminal/drawer-context';

import BlockTerminalTab from '#/components/Terminal/BlockTerminalTab';
import {
  SIDE_PANEL_CHAT_MAX_WIDTH,
  SIDE_PANEL_CHAT_MIN_WIDTH,
  SIDEBAR_MAX_WIDTH,
  SIDEBAR_MIN_WIDTH,
} from '#/components/Terminal/layout-constants';
import { LocalTabFiltersProvider } from '#/components/UnifiedRfqs/UnifiedRfqs';

/**
 * Right-rail mount of the Block Terminal. Renders only when the user has
 * pinned the terminal as a side panel and the drawer is open. Both the
 * bottom-panel tabs and the side panel are visible simultaneously, similar
 * to the Paradex layout.
 *
 * The panel's outer width = sessions sidebar width + chat-pane width.
 * The chat-pane width is resized by dragging the panel's left edge; the
 * sessions sidebar width is resized via the middle divider inside the
 * terminal (see `BlockTerminalTab`). Both widths persist per-user.
 */
export default function BlockTerminalSidePanel() {
  const enabled = useIsFeatureFlagEnabled(FEATURE_BLOCK_TERMINAL);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const drawerState = useTerminalDrawerState();
  const drawerActions = useTerminalDrawerActions();

  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startSidebar: number;
    // Effective contribution of the sidebar to the panel total — 0 when
    // the sidebar is collapsed, sidebarWidth otherwise. Captured at
    // pointer-down so a mid-drag collapse toggle (unlikely) doesn't flip
    // the math halfway through a gesture.
    startSidebarContribution: number;
    startChat: number;
    currentSidebar: number;
    currentChat: number;
  } | null>(null);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      dragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        startSidebar: drawerState.sidebarWidth,
        startSidebarContribution: drawerState.sidebarCollapsed
          ? 0
          : drawerState.sidebarWidth,
        startChat: drawerState.sidePanelChatWidth,
        currentSidebar: drawerState.sidebarWidth,
        currentChat: drawerState.sidePanelChatWidth,
      };
      setIsDragging(true);
    },
    [
      drawerState.sidebarWidth,
      drawerState.sidebarCollapsed,
      drawerState.sidePanelChatWidth,
    ],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const r = dragRef.current;
      if (r == null || r.pointerId !== e.pointerId) return;
      // Defensive: if no mouse button is held the drag has effectively
      // ended without a paired pointerup (release outside the window,
      // lost pointer-capture, OS interrupt). Without this guard, bare
      // hover over the handle keeps resizing because dragRef stays set
      // from the previous gesture. `e.buttons` is the bitmask of
      // currently-pressed buttons (0 = none).
      if (e.buttons === 0) {
        dragRef.current = null;
        setIsDragging(false);
        return;
      }
      // The outer left edge targets the panel's *total* width. Dragging
      // left (clientX decreasing) grows the panel. Once the chat pane
      // hits its min, the sidebar starts giving up width too — so the
      // panel can keep shrinking even when the chat is already at min
      // and the sidebar is wide.
      const startTotal = r.startSidebarContribution + r.startChat;
      const desiredTotalRaw = startTotal - (e.clientX - r.startX);

      if (r.startSidebarContribution === 0) {
        // Sidebar collapsed — total is just the chat width.
        const nextChat = clamp(
          desiredTotalRaw,
          SIDE_PANEL_CHAT_MIN_WIDTH,
          SIDE_PANEL_CHAT_MAX_WIDTH,
        );
        r.currentChat = nextChat;
        drawerActions.setSidePanelChatWidth(nextChat);
        return;
      }

      // Sidebar visible. Distribute desired_total between chat and
      // sidebar so the chat stays at its starting width whenever the
      // joint constraints allow, then chat shrinks/grows first, and
      // sidebar absorbs whatever's left. Symmetric — the sidebar
      // restores to start_sidebar as soon as chat moves back to its
      // start, so dragging back and forth doesn't permanently warp the
      // split.
      const desiredTotal = clamp(
        desiredTotalRaw,
        SIDEBAR_MIN_WIDTH + SIDE_PANEL_CHAT_MIN_WIDTH,
        SIDEBAR_MAX_WIDTH + SIDE_PANEL_CHAT_MAX_WIDTH,
      );
      const nextChat = clamp(
        desiredTotal - r.startSidebar,
        SIDE_PANEL_CHAT_MIN_WIDTH,
        SIDE_PANEL_CHAT_MAX_WIDTH,
      );
      const nextSidebar = clamp(
        desiredTotal - nextChat,
        SIDEBAR_MIN_WIDTH,
        SIDEBAR_MAX_WIDTH,
      );
      r.currentChat = nextChat;
      r.currentSidebar = nextSidebar;
      drawerActions.setSidePanelChatWidth(nextChat);
      // Always push the latest computed sidebar width through, even when
      // it equals r.startSidebar. The previous "skip if === startSidebar"
      // guard was incorrect: after sidebar had already moved earlier in
      // the same drag, a later frame whose target happened to be the
      // start value would skip the write and leave state stuck at the
      // earlier moved value — visible as the panel "jumping back" on
      // pointer-up. React dedupes same-value setState calls, so writing
      // every frame is free.
      drawerActions.setSidebarWidth(nextSidebar);
    },
    [drawerActions],
  );

  const onPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const r = dragRef.current;
      if (r == null || r.pointerId !== e.pointerId) return;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        // already released — ignore
      }
      // Persist once at end of drag — pointer-move only wrote to
      // in-memory state to avoid 60-writes-per-second to localStorage.
      drawerActions.commitSidePanelChatWidth(r.currentChat);
      drawerActions.commitSidebarWidth(r.currentSidebar);
      dragRef.current = null;
      setIsDragging(false);
    },
    [drawerActions],
  );

  if (!isAuthenticated) return null;
  if (!enabled) return null;
  if (drawerState.pinMode !== 'side-panel') return null;
  if (!drawerState.isOpen) return null;

  const sidebarContribution = drawerState.sidebarCollapsed
    ? 0
    : drawerState.sidebarWidth;
  const totalWidth = drawerState.sidePanelChatWidth + sidebarContribution;

  return (
    <Panel $width={totalWidth} $dragging={isDragging}>
      <LeftEdgeHandle
        $dragging={isDragging}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        aria-label="Resize terminal side panel"
        role="separator"
        aria-orientation="vertical"
      />
      <LocalTabFiltersProvider storageKey="trade-tape-rfq-tab-filters">
        <UnifiedRfqTradeTapeProvider>
          <BlockTerminalTab />
        </UnifiedRfqTradeTapeProvider>
      </LocalTabFiltersProvider>
    </Panel>
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

const Panel = styled.aside<{
  readonly $width: number;
  readonly $dragging: boolean;
}>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: ${({ $width }) => $width}px;
  max-width: 100vw;
  background: var(--bg-color-container, #0e131a);
  border-left: 1px solid var(--border-color, rgba(255, 255, 255, 0.08));
  display: flex;
  flex-direction: column;
  z-index: 900;
  /* No drop-shadow — the LayoutContainer shrinks the main app by the
     panel's exact width, so the panel sits flush against the main view
     instead of floating above it. A shadow here would only reinforce the
     "modal-over-content" feel we are explicitly moving away from. */
  /* Disable the width animation during a drag so the panel tracks the
     cursor 1:1 instead of lagging behind it. */
  transition: ${({ $dragging }) => ($dragging ? 'none' : 'width 0.18s ease')};
`;

const LeftEdgeHandle = styled.div<{ readonly $dragging: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 6px;
  margin-left: -3px;
  cursor: ew-resize;
  touch-action: none;
  z-index: 1;
  transition: background-color 0.2s ease;
  background-color: ${({ $dragging }) =>
    $dragging ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;
